import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { IContractInfo } from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import CustomRHFAutocomplete from 'src/components/hook-form/CustomRHFAutocomplete';
import uuid from 'react-uuid';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Label from 'src/components/Label';
import { DateConverter } from 'src/sections/common/DateConverter';
import CalculateDuration from 'src/sections/common/CalculateDuration';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';

// ----------------------------------------------------------------------

export default observer(function ExpirationSettlementNewEditForm() {
  const { ContractInfoStore } = useStore();
  const [floorId, setFloorId] = useState<any>();
  const { translate } = useLocales();
  const { ExpirationSettlement, selectedContractInfoDetail, clearSelectedContractInfo } =
    ContractInfoStore;

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewContractInfoSchema = Yup.object().shape({

  });

  const defaultValues = useMemo<IContractInfo>(
    () => ({
      contractId: selectedContractInfoDetail?.contractId,
      terminationDescription: selectedContractInfoDetail?.terminationDescription || '',
      terminationDate: selectedContractInfoDetail?.terminationDate || new Date(),
      settlmentDeductionAmount: selectedContractInfoDetail?.settlmentDeductionAmount || 0,
      deductedAmountFromAdvancePayment:
        selectedContractInfoDetail?.deductedAmountFromAdvancePayment || 0,
      manuallyPaidAmount: selectedContractInfoDetail?.manuallyPaidAmount || 0,
      remainAmount: selectedContractInfoDetail?.remainAmount || 0,
      refundedAdvancePayment: selectedContractInfoDetail?.refundedAdvancePayment || 0,
      deductionDescription: selectedContractInfoDetail?.deductionDescription || '',
    }),
    [selectedContractInfoDetail]
  );

  const methods = useForm<IContractInfo>({
    resolver: yupResolver(NewContractInfoSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = (data: IContractInfo) => {
    data.terminationDate = new Date(data.terminationDate!);
    console.log(data);

    ExpirationSettlement(data)
      .then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.ContractInfo.list);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
             
              <RHFTextField
                name="settlmentDeductionAmount"
                label={translate('Terminate.SettlmentDeductionAmount')}
                showAsterisk={true}
              />

              <RHFTextField
                name="deductedAmountFromAdvancePayment"
                label={translate('Terminate.DeductedAmountFromAdvancePayment')}
                showAsterisk={true}
              />

              {/* <RHFTextField
                name="initialRentAmount"
                label={translate('Terminate.InitialRentAmount')}
                showAsterisk={true}
              /> */}

              <RHFTextField
                name="manuallyPaidAmount"
                label={translate('Terminate.ManuallyPaidAmount')}
                showAsterisk={true}
              />
              <RHFTextField
                name="remainAmount"
                label={translate('Terminate.RemainAmount')}
                showAsterisk={true}
              />
              <RHFTextField
                name="refundedAdvancePayment"
                label={translate('Terminate.RefundedAdvancePayment')}
                showAsterisk={true}
              />
              <RHFTextField
                name="deductionDescription"
                label={translate('Terminate.DeductionDescription')}
                showAsterisk={true}
              />

             
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={<Iconify icon="eva:edit-fill" />}
              >
                {`${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedContractInfo();
                  navigate(PATH_DASHBOARD.ContractInfo.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* اطلاعات قرارداد قبلی */}
              <Label color="warning">
                <Typography variant="h6" align="center">
                  {translate('contractsInfo.ContractInfo')}
                </Typography>
              </Label>

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>{translate('Customer.CustomerName')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('Shop.ShopCode')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.shopCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.StartDate')}</TableCell>
                    <TableCell>
                      <DateConverter date={selectedContractInfoDetail?.contractStartDate} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.EndDate')}</TableCell>
                    <TableCell>
                      <DateConverter date={selectedContractInfoDetail?.contractEndDate} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.ContractDuration')}</TableCell>
                    <TableCell>
                      {CalculateDuration(
                        translate,
                        selectedContractInfoDetail?.contractStartDate,
                        selectedContractInfoDetail?.contractEndDate
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.DurationOfDiscount')}</TableCell>
                    <TableCell>
                      {CalculateDuration(
                        translate,
                        selectedContractInfoDetail?.discountStartDate,
                        selectedContractInfoDetail?.discountEndDate
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.FinalRentPerMonth')}</TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.finalRentPerMonth}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.AdvancePayment')}</TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.advancePayment}{' '}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.TotalContractDurationRent')}</TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.totalContractDurationRent}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {translate('contractsInfo.TotalContractDurationPaidRent')}
                    </TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.totalContractDurationPaidRent}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
